import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useRef } from "react";

import { IgAzurePrimitives } from "@intelligems/ig-design-tokens/constants";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { DataType } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";

import { UpsellProductRow } from "@src/features/analytics/types";

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .ag-header-row`]: {
    backgroundColor: IgAzurePrimitives[100],
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
  },
  [`& .ag-header-cell-text`]: {
    color: `${theme.palette.text.secondary} !important`,
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "18px",
    letterSpacing: ".25px",
  },
  [`& .ag-cell`]: {
    fontSize: "14px",
    alignContent: "center",
  },
  [`& .ag-cell:not(:last-child)`]: {
    borderRight: `1px solid ${theme.igPrimitives.gray[600]}`,
  },
}));

interface Column {
  field: keyof UpsellProductRow;
  headerName: string;
  dataFormat: DataType | "noFormatting";
  numeric?: boolean;
  flex?: number;
  sort?: "desc";
}

const columns: Column[] = [
  { field: "offer", headerName: "Offer", dataFormat: "noFormatting", flex: 2 },
  { field: "testGroup", headerName: "Test Group", dataFormat: "noFormatting" },
  {
    field: "unitsSold",
    headerName: "Units Sold",
    dataFormat: "numberNoDecimal",
    numeric: true,
  },
  {
    field: "upsellRevenue",
    headerName: "Upsell Revenue",
    dataFormat: "currency",
    numeric: true,
  },
  {
    field: "upsellProfit",
    headerName: "Upsell Profit",
    dataFormat: "currency",
    numeric: true,
    sort: "desc",
  },
];

interface UpsellProductsTableProps {
  rows: UpsellProductRow[];
  isLoading: boolean;
  emptyMessage: string;
}

export const UpsellProductsTable = ({
  rows,
  isLoading,
  emptyMessage,
}: UpsellProductsTableProps) => {
  const graphValueFormatter = useGraphValueFormatter();
  const gridRef = useRef<AgGridReact>(null);

  const format = (
    value: number | string,
    dataFormat: DataType | "noFormatting"
  ) => {
    if (dataFormat === "noFormatting") {
      return value;
    }
    // Render empty/zero values as "-" rather than "$0.00".
    if (!value) {
      return "-";
    }
    return graphValueFormatter({
      isAxisTick: false,
      value: Number(value),
      configDataTypes: [dataFormat],
    });
  };

  const defaultColDef: ColDef = {
    flex: 1,
    resizable: true,
    menuTabs: [],
    suppressMovable: true,
    wrapHeaderText: true,
    suppressHeaderMenuButton: true,
    sortable: true,
  };

  const columnDefs: ColDef<UpsellProductRow>[] = columns.map((column) => ({
    field: column.field,
    headerName: column.headerName,
    flex: column.flex ?? 1,
    sort: column.sort,
    cellStyle: { fontSize: "14px", alignContent: "center" },
    // eslint-disable-next-line react/no-unstable-nested-components
    headerComponent: () => (
      <Grid
        container
        sx={{ justifyContent: column.numeric ? "flex-end" : "flex-start" }}
      >
        <IgTypography sx={{ fontWeight: 600, textAlign: "end" }}>
          {column.headerName}
        </IgTypography>
      </Grid>
    ),
    // eslint-disable-next-line react/no-unstable-nested-components
    cellRenderer: (params: { value: number }) => {
      if (column.numeric) {
        return (
          <Grid container width={"100%"} sx={{ justifyContent: "flex-end" }}>
            <IgTypography sx={{ fontWeight: 400 }}>
              {format(params.value, column.dataFormat)}
            </IgTypography>
          </Grid>
        );
      }
      // Text columns: constrain width and ellipsize; the full value is
      // revealed on hover via the tooltip.
      return (
        <Grid container width={"100%"}>
          <IgTooltip
            title={String(params.value ?? "")}
            wrapperStyle={{
              display: "block",
              width: "100%",
              overflow: "hidden",
            }}
          >
            <IgTypography
              noWrap
              sx={{ fontWeight: 400, display: "block", maxWidth: "100%" }}
            >
              {format(params.value, column.dataFormat)}
            </IgTypography>
          </IgTooltip>
        </Grid>
      );
    },
  }));

  return (
    <StyledGrid size={"grow"} sx={{ width: "100%" }}>
      <AgGridReact<UpsellProductRow>
        ref={gridRef}
        defaultColDef={defaultColDef}
        rowData={rows}
        columnDefs={columnDefs}
        rowHeight={48}
        suppressDragLeaveHidesColumns={true}
        suppressCellFocus={true}
        domLayout={"autoHeight"}
        loading={isLoading}
        overlayNoRowsTemplate={`<div>${emptyMessage}</div>`}
      />
    </StyledGrid>
  );
};
